import { useCallback, useEffect, useState } from 'react'
import { Divider } from 'semantic-ui-react'
import { UploadDocumentType } from '@/constants/businessConstants'
import { Link, Text } from '@/componentsV1/BaseComponents'
import { StepHeaders } from '@/features/bookkeeping/step-header'
import { DocumentUploadCard } from '@/componentsV1/FileUpload/DocumentUploadCard'
import FileUploadModal, {
  UploadedFile,
} from '@/componentsV1/FileUpload/FileUploadModal'
import {
  getInstructionsLink,
  getMatchingMerchants,
  IMerchantDocument,
} from './documents-map'
import { IDocumentsStepContext } from '../types'
import { NotRelevantDocuments } from '../not-relevant-documents'
import { UserDocument } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { useDocuments } from '../../hooks/use-documents'
import './styles.scss'
import { UserDocumentCategoryIdentifier } from '@/features/admin/user-document-categories/userDocumentCategory.constants'
import { UserProcessingFee } from '@/features/bookkeeping/end-of-year-review/actions'

export interface IProcessingFeesDocumentsProps {
  processingFees?: UserProcessingFee[]
  documents: UserDocument[]
  taxYear: string
  stepContext: IDocumentsStepContext
  setStepContext: (context: IDocumentsStepContext) => void
}

export const ProcessingFeesDocuments = ({
  processingFees = [],
  documents,
  taxYear,
  stepContext,
  setStepContext,
}: IProcessingFeesDocumentsProps) => {
  const [uniqueDocumentMerchants, setUniqueDocumentMerchants] = useState<
    IMerchantDocument[]
  >([])
  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false)
  const documentCategoryMap = new Map<
    UploadDocumentType,
    UserDocumentCategoryIdentifier[]
  >()
  documentCategoryMap.set(UploadDocumentType.TAX, [
    UserDocumentCategoryIdentifier.form1099k,
    UserDocumentCategoryIdentifier.form1099nec,
  ])
  documentCategoryMap.set(UploadDocumentType.BOOKKEEPING, [])
  const [fileUpload, setFileUpload] = useState<{
    merchantName: string
    documentCategoryName: string
    documentName: string
    documentCategoryNameInternal?: string
    type?: UploadDocumentType
  } | null>(null)

  const handleFileUpload = (
    merchantName?: string,
    documentCategoryName?: string,
    documentName?: string,
    documentCategoryNameInternal?: string,
    type?: UploadDocumentType
  ) => {
    setIsFileUploadModalOpen(true)

    if (merchantName && documentCategoryName && documentName) {
      setFileUpload({
        merchantName,
        documentCategoryName,
        documentName,
        documentCategoryNameInternal,
        type,
      })
    } else {
      setFileUpload(null)
    }
  }

  const onFileUploaded = useCallback(
    (uploadedFile: UploadedFile) => {
      // Update step context based on the uploaded file
      if (!fileUpload) {
        const otherDocumentIds = [
          ...(stepContext.otherDocumentIds ?? []),
          uploadedFile.id,
        ]
        setStepContext({ ...stepContext, otherDocumentIds })
      } else {
        const documentId = uploadedFile.id
        const categoryCode = fileUpload.documentCategoryName
        const name = fileUpload.documentName
        const provider = fileUpload.merchantName
        const documents = [
          ...(stepContext.documents?.filter(
            (doc) =>
              !(
                doc.categoryCode === categoryCode &&
                doc.provider === provider &&
                doc.name === name
              )
          ) ?? []),
          {
            documentId,
            categoryCode,
            name,
            provider,
          },
        ]
        setStepContext({ ...stepContext, documents })
      }
    },
    [fileUpload, stepContext, setStepContext]
  )

  useEffect(() => {
    const uniqueMerchants = getMatchingMerchants(taxYear, processingFees, [])
    setUniqueDocumentMerchants(uniqueMerchants)
  }, [taxYear, processingFees])

  const { filterDocuments, getStatus } = useDocuments({
    stepContextDocuments: stepContext?.documents,
    userDocuments: documents,
  })

  return (
    <div className="eoybk__processing-fees__container">
      <FileUploadModal
        close={() => setIsFileUploadModalOpen(false)}
        documentType={fileUpload?.type || UploadDocumentType.OTHER}
        categoryInternalName={
          fileUpload?.documentCategoryName &&
          fileUpload?.documentCategoryName === 'mix_documents'
            ? undefined
            : fileUpload?.documentCategoryName
        }
        open={isFileUploadModalOpen}
        userFacing
        isUploadOnly={
          fileUpload?.documentCategoryName === 'mix_documents' ? false : true
        }
        setUploadedFile={onFileUploaded}
        isMixedType={
          fileUpload?.documentCategoryName === 'mix_documents' ? true : false
        }
        internalCategoryNameFlag={fileUpload?.documentCategoryNameInternal}
        documentCategoryMap={documentCategoryMap}
        year={taxYear}
      />

      <StepHeaders
        customColor="yellow"
        description="Next, we need reports that show any processing fees deducted from your income. This helps us record those fees as business expenses."
        imageSrc="https://heard-images.s3.amazonaws.com/assets/credit_card.svg"
        imageAlt="stacked checkbooks"
        imageWidth={180}
        imageHeight={180}
        kickerText="YEAR-END DOCUMENTS"
        title="Processing fees"
      >
        <Link
          href="https://support.joinheard.com/hc/en-us/articles/19809547067543-Obtaining-and-uploading-year-end-documents#merchant_fees"
          newPage
        >
          Learn more
        </Link>
      </StepHeaders>

      <div>
        <Text>
          The name of this report varies from vendor to vendor. Please see the
          instructions below for each.
        </Text>
      </div>

      <Divider className="eoybk__processing-fees__divider" />

      <Text as="h2">Upload income source reports</Text>

      <Text>
        If you don’t have a document on hand, you may skip for now. You’ll be
        able to view instructions and upload everything at the end.
      </Text>

      <div className="eoybk__processing-fees__document-cards">
        <NotRelevantDocuments
          stepContext={stepContext}
          setStepContext={setStepContext}
          renderDocumentUploadCards={(isMarkedNotRelevant, handleNotRelevant) =>
            uniqueDocumentMerchants.map(
              ({
                id,
                merchantName,
                documentCategoryName,
                documentName,
                documentCategoryNameInternal,
                type,
              }) => (
                <DocumentUploadCard
                  key={`${id}-${documentName}`}
                  name={`${merchantName} - ${documentName}`}
                  description="This captures income, processing fees, and their timing."
                  href={getInstructionsLink(
                    documentCategoryName,
                    merchantName,
                    taxYear
                  )}
                  notRelevant={isMarkedNotRelevant?.(
                    documentCategoryName,
                    merchantName,
                    documentName
                  )}
                  setChecked={() =>
                    handleNotRelevant?.(
                      documentCategoryName,
                      merchantName,
                      documentName
                    )
                  }
                  status={getStatus(
                    documentCategoryName,
                    merchantName,
                    isMarkedNotRelevant,
                    documentName
                  )}
                  documents={filterDocuments(
                    documentCategoryName,
                    merchantName,
                    documentName
                  )}
                  handleUploadBtn={() =>
                    handleFileUpload(
                      merchantName,
                      documentCategoryName,
                      documentName,
                      documentCategoryNameInternal,
                      type
                    )
                  }
                />
              )
            )
          }
        />

        <DocumentUploadCard
          name="Other documents"
          description="Upload any other documents you'd like to share with the bookkeeping team here."
          status="disabled"
          documents={documents.filter((doc) =>
            (stepContext?.otherDocumentIds ?? []).includes(doc.id)
          )}
          handleUploadBtn={() => handleFileUpload()}
        />
      </div>
    </div>
  )
}
